import { useAppDisplayModeContext, useStyles } from '@archipro-design/aria';
import type { ImageColumnProps } from '@modules/page-builder/types';
import { TRIO_COLUMN_WIDTH_MD } from '@modules/page-builder/config/column-constants';
import DetailSingleImageShell from '../detail-single-image-shell/DetailSingleImageShell';
import * as S from './TrioImageModule.style';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import ImageWithPaintPicker from '../../common/image-with-paint-picker/ImageWithPaintPicker';
import { ImageModuleExtraSpacing } from '@modules/page-builder/component/detail/detail-modules/DetailModules.style';

interface TrioImageModuleProps {
    columns: ImageColumnProps[];
    /** Project detail revamp (BC-4725) — restyles the caption to the Figma text style. */
    isProjectRevamp?: boolean;
}

/**
 * A rendered Page Builder module for displaying three images in a grid layout.
 *
 * The module displays three images side by side on desktop and stacked on mobile.
 * Each image can have captions, tags, and interactive elements like paint picker integration.
 *
 * The editor version is at
 * {@link packages/web/app/modules/page-builder/component/edit/trio-image-module/TrioImageModule.tsx}
 */
const TrioImageModule = ({
    columns,
    isProjectRevamp,
}: TrioImageModuleProps) => {
    const { mobile } = useAppDisplayModeContext();

    const { css } = useStyles({
        columns: columns.filter((c) => mobile && c.imageUrl).length,
        mobile,
        isProjectRevamp,
    });

    return (
        <div
            className={css(S.TrioImageModuleContainer, ImageModuleExtraSpacing)}
        >
            {columns.map((column) => {
                const isMedium = column.dimension.width >= TRIO_COLUMN_WIDTH_MD;
                const isSmall = !isMedium;
                if (mobile && !column.imageUrl) {
                    return null;
                }

                return (
                    <div
                        key={column.id}
                        className={css(S.TrioImageContainerImage(isMedium))}
                    >
                        {column?.imageUrl && (
                            <DetailSingleImageShell
                                caption={column.caption}
                                dimension={column.dimension}
                                libraryImage={column?.libraryImage}
                                tags={column.tags}
                                uniqueApprovedTags={column.uniqueApprovedTags}
                                imageURL={column.imageUrl}
                                columnId={column.id}
                                isProjectRevamp={isProjectRevamp}
                                moduleType="TrioImageModule"
                            >
                                <ImageWithPaintPicker
                                    src={column.imageUrl}
                                    width={column.dimension.width}
                                    height={column.dimension.height}
                                    layout={'fill'}
                                    objectFit={'cover'}
                                    className="overflow-hidden md:rounded-2"
                                    sizes={
                                        isSmall
                                            ? getImagesSizes(800)
                                            : getImagesSizes(1050)
                                    }
                                    alt={
                                        column.caption ||
                                        column?.libraryImage?.Title ||
                                        ''
                                    }
                                />
                            </DetailSingleImageShell>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default TrioImageModule;
